import React, { useCallback, useEffect, useMemo, useState } from "react";
import Segmented from "@/components/Segmented";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, PackageCheck, RotateCcw, Loader2, ChevronDown, ChevronRight, Check, Search } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { api } from "../lib/api";
import { useRole } from "../context/RoleContext";
import MaterialTicketsPanel from "./MaterialTicketsPanel";
import WaitingPanel from "./WaitingPanel";
import MaterialDashboard from "./MaterialDashboard";
import MaterialStatusGrid from "./MaterialStatusGrid";
import SurplusSweep from "./SurplusSweep";
import { toast } from "sonner";

const REASONS = [["excess", "Excess material"], ["wrong_material", "Wrong material"],
                 ["damaged", "Damaged"], ["other", "Other"]];
const CONFIRM_ROLES = ["admin", "management", "warehouse"];

function Num({ v, bad }) {
  return <td className={`p-2 text-right tabular-nums ${bad ? "text-red-700 font-bold" : ""}`}>{v}</td>;
}

// Cluster 16B — one row per product: what left the shop, what is onsite,
// what got installed, what came back and what the math cannot explain.
export default function MaterialTrackingView({ projectId, focusTicket = "" }) {
  const { role } = useRole();
  const { user } = useAuth();
  const who = user?.name || user?.email || "";
  const canConfirm = CONFIRM_ROLES.includes(String(role || "").toLowerCase());

  const [rows, setRows] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [scope, setScope] = useState("");
  const [ticketId, setTicketId] = useState("");
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [search, setSearch] = useState("");
  const [openKey, setOpenKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState("overview");
  // FLD-5 — jumping from a follow-up in the waiting list opens that ticket.
  const [focus, setFocus] = useState(focusTicket);
  useEffect(() => { setFocus(focusTicket); }, [focusTicket]);
  useEffect(() => { if (focusTicket) setSubTab("tickets"); }, [focusTicket]);   // FLD-1 — tickets first
  const [returnFor, setReturnFor] = useState(null);
  const [flagFor, setFlagFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, tk] = await Promise.all([
        api.get("/material/tracking", { params: { project_id: projectId, ticket_id: ticketId } }),
        api.get("/picking-tickets", { params: { project_id: projectId } }),
      ]);
      setRows(t.data || []);
      setTickets(tk.data || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [projectId, ticketId]);
  useEffect(() => { load(); }, [load]);

  const scopes = useMemo(() => [...new Set(rows.map((r) => r.scope_type).filter(Boolean))].sort(), [rows]);
  const q = search.trim().toLowerCase();
  const shown = useMemo(() => rows.filter((r) => (!scope || r.scope_type === scope)
    && (!onlyIssues || r.has_discrepancy)
    && (!q || [r.scope_type, r.product_code, r.product_description].some((v) => String(v || "").toLowerCase().includes(q)))),
    [rows, scope, onlyIssues, q]);

  const totals = useMemo(() => shown.reduce((a, r) => ({
    pulled: a.pulled + r.pulled, delivered: a.delivered + r.delivered,
    installed: a.installed + r.installed, remaining: a.remaining + r.remaining_onsite,
    returned: a.returned + r.returned, discrepancy: a.discrepancy + r.discrepancy,
  }), { pulled: 0, delivered: 0, installed: 0, remaining: 0, returned: 0, discrepancy: 0 }), [shown]);

  const confirmReturn = async (id) => {
    if (!who.trim()) { toast.error("Sign in first — a return has to be confirmed by a named person"); return; }
    try {
      await api.post(`/material/returns/${id}/confirm`, null, { params: { by: who, role } });
      toast.success("Return confirmed as received at the shop");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not confirm the return"); }
  };

  return (
    <div className="space-y-3" data-testid="material-tracking-view">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="text-base md:text-lg font-semibold text-gray-900 flex items-center gap-2">
          <PackageCheck className="w-4 h-4 text-blue-700" /> Material tracking
        </h2>
        {subTab === "products" && (
          <span className="text-xs text-gray-500">installed comes straight from the Install tab — remaining onsite follows it</span>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…"
                 data-testid="material-search" className="h-8 w-56 pl-8 text-xs" />
        </div>
        <select value={scope} onChange={(e) => setScope(e.target.value)} data-testid="material-scope-filter"
                className="h-8 text-xs border border-input rounded px-2 bg-background text-foreground">
          <option value="">All scopes</option>
          {scopes.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={ticketId} onChange={(e) => setTicketId(e.target.value)} data-testid="material-ticket-filter"
                className="h-8 text-xs border border-input rounded px-2 bg-background text-foreground">
          <option value="">All tickets</option>
          {tickets.map((t) => <option key={t.id} value={t.id}>{t.ticket_number}</option>)}
        </select>
        <label className="text-xs flex items-center gap-1 text-gray-700" data-testid="material-issues-toggle">
          <input type="checkbox" checked={onlyIssues} onChange={() => setOnlyIssues(!onlyIssues)} />
          Discrepancies
        </label>
      </div>
      <div data-testid="material-subtabs">
        <Segmented
          options={[["overview", "Status"], ["tickets", "Pull tickets"],
                    ["waiting", "Waiting on material"], ["surplus", "Surplus"],
                    ["products", "Product tracking"]]
            .map(([k, label]) => ({ key: k, label, testId: `material-subtab-${k}` }))}
          value={subTab}
          onChange={setSubTab}
        />
      </div>
      {subTab === "overview" && (
        <>
          <MaterialDashboard projectId={projectId} onGoTo={(k) => setSubTab(k)} />
          <MaterialStatusGrid projectId={projectId} />
        </>
      )}
      {subTab === "tickets" && (
        <MaterialTicketsPanel projectId={projectId} focusTicket={focus} search={search} />
      )}
      {subTab === "surplus" && <SurplusSweep projectId={projectId} />}
      {subTab === "waiting" && (
        <WaitingPanel projectId={projectId}
                      onOpenTicket={(number) => { setSubTab("tickets"); setFocus(number); }} />
      )}
      {subTab === "products" && ticketId && (
        <div className="text-[11px] text-amber-700" data-testid="material-ticket-filter-hint">
          Filtered to one ticket — returns and flags recorded without a ticket are not counted here.
        </div>
      )}

      <div className={`border border-gray-200 rounded-lg bg-white overflow-x-auto ${subTab === "products" ? "" : "hidden"}`}>
        <table className="w-full text-xs min-w-[1080px]">
          <thead className="bg-gray-100 text-gray-600 uppercase text-[10px]">
            <tr>
              <th className="p-2"></th><th className="text-left p-2">Scope</th><th className="text-left p-2">Code</th>
              <th className="text-left p-2">Description</th>
              <th className="text-right p-2">Requested</th><th className="text-right p-2">Pulled</th>
              <th className="text-right p-2">Delivered</th><th className="text-right p-2">Installed</th>
              <th className="text-right p-2">Remaining onsite</th><th className="text-right p-2">Returned</th>
              <th className="text-right p-2">Damaged</th><th className="text-right p-2">Missing</th>
              <th className="text-right p-2">Discrepancy</th><th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const key = `${r.scope_type}|${r.product_code}`;
              const isOpen = openKey === key;
              return (
                <React.Fragment key={key}>
                  <tr className={`border-t border-gray-100 ${r.has_discrepancy ? "bg-red-50" : ""}`}
                      data-testid={`material-row-${r.product_code}`}
                      data-discrepancy={r.has_discrepancy ? "1" : "0"}>
                    <td className="p-2">
                      <button onClick={() => setOpenKey(isOpen ? "" : key)} data-testid={`material-expand-${r.product_code}`}>
                        {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      </button>
                    </td>
                    <td className="p-2">{r.scope_type}</td>
                    <td className="p-2 font-mono">{r.product_code}</td>
                    <td className="p-2 max-w-[240px] truncate" title={r.product_description}>{r.product_description}</td>
                    <Num v={r.requested} />
                    <Num v={r.pulled} />
                    <Num v={r.delivered} />
                    <Num v={r.installed} bad={r.over_installed > 0} />
                    <Num v={r.remaining_onsite} bad={r.remaining_onsite < 0} />
                    <td className="p-2 text-right tabular-nums">
                      {r.returned}
                      {r.returned_in_transit > 0 && (
                        <span className="ml-1 text-[10px] px-1 rounded bg-amber-100 text-amber-800 font-semibold"
                              data-testid={`material-intransit-${r.product_code}`}>+{r.returned_in_transit} in transit</span>
                      )}
                    </td>
                    <Num v={r.damaged} bad={r.damaged > 0} />
                    <Num v={r.missing} bad={r.missing > 0} />
                    <td className={`p-2 text-right tabular-nums ${r.discrepancy > 0 ? "text-red-700 font-bold" : ""}`}
                        data-testid={`material-discrepancy-${r.product_code}`}>
                      {r.discrepancy > 0 && <AlertTriangle className="w-3 h-3 inline mr-1 -mt-0.5" />}{r.discrepancy}
                    </td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" className="h-7 mr-1" onClick={() => setReturnFor(r)}
                              data-testid={`material-return-btn-${r.product_code}`}>
                        <RotateCcw className="w-3 h-3 mr-1" /> Return
                      </Button>
                      <Button size="sm" variant="outline" className="h-7" onClick={() => setFlagFor(r)}
                              data-testid={`material-flag-btn-${r.product_code}`}>
                        Flag
                      </Button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-gray-50">
                      <td colSpan={14} className="p-3 space-y-3" data-testid={`material-detail-${r.product_code}`}>
                        <div className="text-[11px] text-gray-600">
                          Remaining onsite = delivered − installed − returned after delivery − damaged/missing.
                          {r.in_transit_gap > 0 && <span className="text-red-700 font-semibold"> {r.in_transit_gap} pulled but never confirmed onsite.</span>}
                          {r.over_installed > 0 && <span className="text-red-700 font-semibold"> {r.over_installed} used beyond what was delivered.</span>}
                          {r.tickets?.length > 0 && <span className="ml-1">Tickets: {r.tickets.join(", ")}</span>}
                        </div>
                        <div>
                          <div className="text-[10px] uppercase text-gray-500 font-medium mb-1">Returns</div>
                          {(r.returns || []).length === 0 ? <div className="text-[11px] text-gray-400">None recorded.</div> : (
                            <div className="space-y-1">
                              {r.returns.map((rt) => (
                                <div key={rt.id} className="flex items-center gap-2 text-[11px]" data-testid={`material-return-row-${rt.id}`}>
                                  <span className="font-semibold tabular-nums">{rt.quantity}</span>
                                  <span className="text-gray-600">{(REASONS.find((x) => x[0] === rt.reason) || [])[1]}</span>
                                  <span className={`px-1 rounded text-[10px] font-semibold ${rt.status === "confirmed"
                                    ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                                    {rt.status === "confirmed" ? "Confirmed" : "In transit"}
                                  </span>
                                  <span className="text-gray-500">by {rt.created_by || "—"}</span>
                                  {rt.note && <span className="text-gray-500 italic truncate max-w-[240px]">“{rt.note}”</span>}
                                  {rt.status !== "confirmed" && (
                                    canConfirm ? (
                                      <Button size="sm" variant="outline" className="h-6 text-[10px]"
                                              onClick={() => confirmReturn(rt.id)}
                                              data-testid={`material-confirm-return-${rt.id}`}>
                                        <Check className="w-3 h-3 mr-1" /> Confirm receipt
                                      </Button>
                                    ) : (
                                      <span className="text-[10px] text-gray-400" data-testid={`material-confirm-blocked-${rt.id}`}>
                                        warehouse or admin confirms receipt
                                      </span>
                                    )
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="text-[10px] uppercase text-gray-500 font-medium mb-1">Damage / missing flags</div>
                          {(r.flags || []).length === 0 ? <div className="text-[11px] text-gray-400">None recorded.</div> : (
                            <div className="space-y-1">
                              {r.flags.map((f) => (
                                <div key={f.id} className="flex items-center gap-2 text-[11px]" data-testid={`material-flag-row-${f.id}`}>
                                  <span className="font-semibold tabular-nums">{f.quantity}</span>
                                  <span className="px-1 rounded text-[10px] font-semibold bg-red-100 text-red-800">{f.kind}</span>
                                  <span className="text-gray-500">by {f.created_by || "—"}</span>
                                  <span className="text-gray-600 italic truncate max-w-[300px]">“{f.note}”</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {shown.length === 0 && (
              <tr><td colSpan={14} className="p-6 text-center text-gray-400" data-testid="material-empty">
                {loading ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "No material pulled for this job yet — generate a pull ticket first."}
              </td></tr>
            )}
          </tbody>
          {shown.length > 0 && (
            <tfoot className="bg-gray-50 border-t border-gray-200 font-semibold">
              <tr data-testid="material-totals">
                <td className="p-2" colSpan={5}>Totals</td>
                <td className="p-2 text-right tabular-nums">{totals.pulled}</td>
                <td className="p-2 text-right tabular-nums">{totals.delivered}</td>
                <td className="p-2 text-right tabular-nums">{totals.installed}</td>
                <td className="p-2 text-right tabular-nums">{totals.remaining}</td>
                <td className="p-2 text-right tabular-nums">{totals.returned}</td>
                <td className="p-2 text-right tabular-nums">{shown.reduce((a, r) => a + r.damaged, 0)}</td>
                <td className="p-2 text-right tabular-nums">{shown.reduce((a, r) => a + r.missing, 0)}</td>
                <td className={`p-2 text-right tabular-nums ${totals.discrepancy > 0 ? "text-red-700" : ""}`}>{totals.discrepancy}</td>
                <td className="p-2"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {returnFor && (
        <ReturnDialog row={returnFor} projectId={projectId} tickets={tickets} actor={who}
                      onClose={() => setReturnFor(null)} onSaved={() => { setReturnFor(null); load(); }} />
      )}
      {flagFor && (
        <FlagDialog row={flagFor} projectId={projectId} tickets={tickets} actor={who}
                    onClose={() => setFlagFor(null)} onSaved={() => { setFlagFor(null); load(); }} />
      )}
    </div>
  );
}

function ticketsWith(tickets, row) {
  return tickets.filter((t) => (t.lines || []).some(
    (l) => l.product_code === row.product_code && l.scope_type === row.scope_type));
}

function ReturnDialog({ row, projectId, tickets, actor, onClose, onSaved }) {
  const opts = ticketsWith(tickets, row);
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("excess");
  const [note, setNote] = useState("");
  const [ticketId, setTicketId] = useState(opts.length === 1 ? opts[0].id : "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/material/returns", {
        project_id: projectId, ticket_id: ticketId, scope_type: row.scope_type,
        product_code: row.product_code, quantity: Number(qty), reason, note, by: actor,
      });
      toast.success("Return recorded — in transit until the shop confirms receipt");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not record the return"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="material-return-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="w-4 h-4 text-blue-700" /> Return to shop — {row.product_code}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-xs text-gray-500">{row.remaining_onsite} onsite by our count. A second person at the shop confirms receipt.</div>
          {!actor.trim() && (
            <div className="text-xs text-red-700" data-testid="material-return-needs-name">
              Sign in first — a return has to be traceable to a person.
            </div>
          )}
          <div>
            <Label className="text-[10px] uppercase text-gray-500">Quantity</Label>
            <Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)}
                   data-testid="material-return-qty" className="h-9" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-gray-500">Reason</Label>
            <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="material-return-reason"
                    className="h-9 w-full text-sm border border-gray-300 rounded px-2 bg-white">
              {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          {opts.length > 0 && (
            <div>
              <Label className="text-[10px] uppercase text-gray-500">Ticket (optional)</Label>
              <select value={ticketId} onChange={(e) => setTicketId(e.target.value)} data-testid="material-return-ticket"
                      className="h-9 w-full text-sm border border-gray-300 rounded px-2 bg-white">
                <option value="">— none —</option>
                {opts.map((t) => <option key={t.id} value={t.id}>{t.ticket_number} · {t.location_label}</option>)}
              </select>
            </div>
          )}
          <div>
            <Label className="text-[10px] uppercase text-gray-500">
              Note {reason === "other" ? "(required)" : "(optional)"}
            </Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                      data-testid="material-return-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="material-return-cancel">Cancel</Button>
          <Button disabled={busy || !Number(qty) || !actor.trim()} onClick={save} data-testid="material-return-save">
            {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Record return
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FlagDialog({ row, projectId, tickets, actor, onClose, onSaved }) {
  const opts = ticketsWith(tickets, row);
  const [qty, setQty] = useState("");
  const [kind, setKind] = useState("damaged");
  const [note, setNote] = useState("");
  const [ticketId, setTicketId] = useState(opts.length === 1 ? opts[0].id : "");
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Photo is over 10MB"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("project_id", projectId);
    fd.append("uploader", actor || "");
    fd.append("file", file);
    try {
      const { data } = await api.post("/completions/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhoto(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not upload the photo"); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/material/flags", {
        project_id: projectId, ticket_id: ticketId, scope_type: row.scope_type,
        product_code: row.product_code, quantity: Number(qty), kind, note,
        photo_path: photo?.storage_path || "", by: actor,
      });
      toast.success(`${qty} ${row.product_code} flagged ${kind}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not flag the material"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="material-flag-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600" /> Flag material — {row.product_code}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label className="text-[10px] uppercase text-gray-500">What happened</Label>
            <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="material-flag-kind"
                    className="h-9 w-full text-sm border border-gray-300 rounded px-2 bg-white">
              <option value="damaged">Damaged</option>
              <option value="missing">Missing</option>
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-gray-500">Quantity</Label>
            <Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)}
                   data-testid="material-flag-qty" className="h-9" />
          </div>
          {opts.length > 0 && (
            <div>
              <Label className="text-[10px] uppercase text-gray-500">Ticket (optional)</Label>
              <select value={ticketId} onChange={(e) => setTicketId(e.target.value)} data-testid="material-flag-ticket"
                      className="h-9 w-full text-sm border border-gray-300 rounded px-2 bg-white">
                <option value="">— none —</option>
                {opts.map((t) => <option key={t.id} value={t.id}>{t.ticket_number} · {t.location_label}</option>)}
              </select>
            </div>
          )}
          <div>
            <Label className="text-[10px] uppercase text-gray-500">Note (required)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                      data-testid="material-flag-note" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-gray-500">
              Photo {kind === "damaged" ? "(required)" : "(optional)"}
            </Label>
            <input type="file" accept="image/*" data-testid="material-flag-photo"
                   onChange={(e) => upload(e.target.files?.[0])} className="block text-xs mt-1" />
            {photo && <div className="text-[11px] text-emerald-700 mt-1" data-testid="material-flag-photo-ok">
              {photo.filename} attached</div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="material-flag-cancel">Cancel</Button>
          <Button disabled={busy || !Number(qty) || !note.trim() || (kind === "damaged" && !photo)}
                  onClick={save} data-testid="material-flag-save">
            {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Flag material
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
